import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Navbar from './components/Navbar';
import Sidenav from './components/Sidenav';
import ChatAssistant from './components/ChatAssistant';
import AuthGuard from './components/AuthGuard';
import Dashboard from './components/routes/Dashboard/Dashboard';
import Settings from './components/routes/Settings/Settings';
import Login from './components/routes/Login/Login';
import ForgotPassword from './components/routes/Login/ForgotPassword';
import EditBusinessProfile from './components/routes/EditBusinessProfile/EditBusinessProfile';
import ChatbotSetup from './components/routes/ChatbotSetup/ChatbotSetup';
import Notifications from './components/routes/Notifications/Notifications';
import QRCode from './components/routes/QRCode/QRCode';
import Onboarding from './components/Onboarding/Onboarding';
import CompleteProfile from './components/CompleteProfile/CompleteProfile';
import ResetPassword from './components/routes/Login/ResetPassword';
import StoreManagerPage from './components/routes/StoreManagerPage/StoreManagerPage';
import StoreCheckInPage from './components/routes/StoreCheckInPage/StoreCheckInPage';
import ChatbotPage from './components/routes/ChatbotPage/ChatbotPage';
import DelegateSignup from './components/routes/DelegateSignup/DelegateSignup';
import './styles/main.scss';

const App: React.FC = () => {
  const [isSidenavCollapsed, setIsSidenavCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pageContent, setPageContent] = useState('');

  const toggleSidenav = () => {
    if (isMobile) {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else {
      setIsSidenavCollapsed(!isSidenavCollapsed);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const content = document.body.innerText || '';
    setPageContent(content);

    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if current URL has delegate invitation parameters
  const isDelegateInvitation = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get('email');
    const type = urlParams.get('type');
    return email && type && (type === 'client' || type === 'store');
  };

  return (
    <Router>
      <Route render={({ location }) => {
        // If this is a delegate invitation, redirect to delegate signup
        if (location.pathname === '/' && isDelegateInvitation()) {
          return <Redirect to={`/delegate-signup${location.search}`} />;
        }

        return (
          <div className={`app-container ${isSidenavCollapsed ? 'sidenav-collapsed' : ''} ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
            {location.pathname !== '/' && 
             location.pathname !== '/forgot-password' && 
             location.pathname !== '/onboarding' && 
             location.pathname !== '/reset-password' && 
             location.pathname !== '/delegate-signup' && 
             !location.pathname.startsWith('/checkin/') && 
             !location.pathname.startsWith('/chatbot/') && (
               <AuthGuard>
                 <>
                   <Navbar toggleSidenav={toggleSidenav} />
                   <Sidenav 
                     toggleSidenav={toggleSidenav} 
                     isCollapsed={isSidenavCollapsed} 
                     isMobileMenuOpen={isMobileMenuOpen}
                     closeMobileMenu={closeMobileMenu}
                     isMobile={isMobile}
                   />
                   <div className="main-content">
                     <TransitionGroup>
                       <CSSTransition key={location.key} classNames="fade" timeout={1800}>
                         <Switch location={location}>
                           <Route path="/dashboard" component={Dashboard} />
                           <Route path="/settings" component={Settings} />
                           <Route path="/edit-business-profile" component={EditBusinessProfile} />
                           <Route path="/chatbot-setup" component={ChatbotSetup} />
                           <Route path="/notifications" component={Notifications} />
                           <Route path="/qr-code" component={QRCode} />
                           <Route path="/store-manager" component={StoreManagerPage} />
                           <Route path="/logout" render={() => <Redirect to="/" />} />
                         </Switch>
                       </CSSTransition>
                     </TransitionGroup>
                   </div>
                 </>
               </AuthGuard>
             )}
            <Route exact path="/" component={Login} />
            <Route path="/forgot-password" component={ForgotPassword} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/onboarding" component={Onboarding} />
            <Route path="/complete-profile" component={CompleteProfile} />
            <Route path="/delegate-signup" component={DelegateSignup} />
            <Route path="/checkin/:hash_id" component={StoreCheckInPage} />
            <Route path="/chatbot/:hash_id" component={ChatbotPage} />
            <ChatAssistant pageContent={pageContent} />
          </div>
        );
      }} />
    </Router>
  );
};

export default App;